/*
 * Ported from FatigueDetector.m (USAARL restricted-sleep study).
 * The dot product of the fatigue detector with a noise-normalized
 * delta-COBRA vector gives an estimate in hours ("unit vector over
 * magnitude," which retains units).
 */

const METRIC_ORDER = [
    'INIT_latency', 'INIT_accel', 'SS_gain',
    'SS_sacc_amp', 'SS_prop_smooth', 'DIR_noise',
    'SPD_slope', 'SPD_noise',
] as const;

type CobraMetric = typeof METRIC_ORDER[number];

const USAARL_FATIGUE_VECTOR: Record<CobraMetric, number> = {
    INIT_latency: 0.67,
    INIT_accel: -1.4,
    SS_gain: -0.010,
    SS_sacc_amp: 0.054,
    SS_prop_smooth: -0.008,
    DIR_noise: 0.57,
    SPD_slope: -0.025,
    SPD_noise: 0.130,
};

const OBVA_WITHIN_SUBJECT_NOISE: Record<CobraMetric, number> = {
    INIT_latency: 3.75,
    INIT_accel: 19.0,
    SS_gain: 0.167,
    SS_sacc_amp: 0.289,
    SS_prop_smooth: 0.065,
    DIR_noise: 1.34,
    SPD_slope: 0.192,
    SPD_noise: 0.60,
};

const SCALED = METRIC_ORDER.reduce((acc, metric) => {
    acc[metric] = USAARL_FATIGUE_VECTOR[metric] / OBVA_WITHIN_SUBJECT_NOISE[metric];
    return acc;
}, {} as Record<CobraMetric, number>);

const SUM_SQUARED_SCALED = METRIC_ORDER.reduce((sum, metric) => sum + SCALED[metric] ** 2, 0);

const FATIGUE_DETECTOR: Record<CobraMetric, number> = METRIC_ORDER.reduce((acc, metric) => {
    acc[metric] = SCALED[metric] / SUM_SQUARED_SCALED;
    return acc;
}, {} as Record<CobraMetric, number>);

/*
 * deltas: current scan's metric value minus the previous scan's, keyed by
 * COBRA metric name. Returns null if any metric used by the detector is
 * missing, so a partial/incomplete pair of scans doesn't produce a
 * misleading estimate.
 */
export function computeFatigueHours(deltas: Partial<Record<string, number>>): number | null {
    let fatigueHours = 0;

    for (const metric of METRIC_ORDER) {
        const delta = deltas[metric];
        if (delta === undefined || Number.isNaN(delta)) return null;

        const scaledDelta = delta / OBVA_WITHIN_SUBJECT_NOISE[metric];
        fatigueHours += scaledDelta * FATIGUE_DETECTOR[metric];
    }

    return fatigueHours;
}
